import { formatCurrency } from "@angular/common";
import { Component, Inject, Input, LOCALE_ID, OnInit, ViewChild } from "@angular/core";
import { ChartConfigValue, ChartJsContainerComponent } from "@tylertech/chart-js";
import { percentScale } from "@tylertech/common-components-module";
import { ChartConfiguration, ChartDataset, Tick, TooltipItem } from "chart.js";
import { IAccountModel } from "../../interfaces/iAccountModel";

@Component({
    selector: "account-horizontal-bar-chart",
    templateUrl: "./accountHorizontalBarChart.tmpl.html",
    styleUrls: [
        "./accountHorizontalBarChart.component.scss"
    ],
    standalone: false
})
export class AccountHorizontalBarChartComponent implements OnInit
{
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    @Input() account: IAccountModel;
    @Input() chartId: string;

    chartConfig: ChartConfiguration<"bar">;

    @Input() overBudget: boolean;
    @Input() pendingLabel: string;

    constructor(
        @Inject(LOCALE_ID) private readonly locale: string)
    { }

    ngOnInit(): void
    {
        if (!this.pendingLabel)
        {
            this.pendingLabel = "Pending";
        }

        this.displayChart();
    }

    private calculatePercentages(): void
    {
        const total = this.getSumOfDetails();
        for (const name of ["Activity", "Encumbrance", "Pending", "Reserves", "BudgetAvailable"])
        {
            this.account.Details[name + "Percent"] = (this.account.Details[name] / total) * 100;
        }
    }

    private getSumOfDetails(): number
    {
        const activity = this.account.Details.Activity || 0;
        const encumbrance = this.account.Details.Encumbrance || 0;
        const pending = this.account.Details.Pending || 0;
        const reserves = this.account.Details.Reserves || 0;
        const available = this.account.Details.BudgetAvailable || 0;

        let percentSpread = activity + encumbrance + pending + reserves;
        if (!this.overBudget)
        {
            percentSpread += available;
        }

        if (percentSpread === 0)
        {
            percentSpread = Math.max(activity, 0) + Math.max(encumbrance, 0) + Math.max(pending, 0)
                + Math.max(reserves, 0) + Math.max(available, 0);
        }
        return percentSpread;
    }

    private displayChart(): void
    {
        this.calculatePercentages();
        const datasets: Array<ChartDataset<"bar">> = [
            {
                data: [
                    this.account.Details.ActivityPercent || 0,
                    this.account.Details.Activity
                ],
                label: "Activity",
                backgroundColor: "#008F8C",
                hoverBackgroundColor: "#008F8C",
                // pointBackgroundColor: "#008F8C",
                // pointHoverBackgroundColor: "#008F8C",
                borderColor: "#008F8C",
                // pointHoverBorderColor: "#008F8C",
            }
        ];

        if (this.account.Details.Encumbrance)
        {
            datasets.push(
                {
                    data: [
                        this.account.Details.EncumbrancePercent || 0,
                        this.account.Details.Encumbrance
                    ],
                    label: "Encumbrances",
                    backgroundColor: "#0D2644",
                    hoverBackgroundColor: "#0D2644",
                    // pointBackgroundColor: "#0D2644",
                    // pointHoverBackgroundColor: "#0D2644",
                    borderColor: "#0D2644",
                    // pointHoverBorderColor: "#0D2644",
                });
        }

        if (this.account.Details.Reserves)
        {
            datasets.push(
                {
                    data: [
                        this.account.Details.ReservesPercent || 0,
                        this.account.Details.Reserves
                    ],
                    label: "Reserves",
                    backgroundColor: "#E36039",
                    hoverBackgroundColor: "#E36039",
                    // pointBackgroundColor: "#E36039",
                    // pointHoverBackgroundColor: "#E36039",
                    borderColor: "#E36039",
                    // pointHoverBorderColor: "#E36039",
                });
        }

        datasets.push(
            {
                data: [
                    this.account.Details.PendingPercent || 0,
                    this.account.Details.Pending
                ],
                label: this.pendingLabel || "Pending",
                backgroundColor: "#245718",
                hoverBackgroundColor: "#245718",
                // pointBackgroundColor: "#245718",
                // pointHoverBackgroundColor: "#245718",
                borderColor: "#245718",
                // pointHoverBorderColor: "#245718",
            },
            {
                data: [
                    this.overBudget
                        ? 0
                        : this.account.Details.BudgetAvailablePercent || 0,
                    this.account.Details.BudgetAvailable
                ],
                label: "Budget Available",
                backgroundColor: "#5C6E83",
                hoverBackgroundColor: "#5C6E83",
                // pointBackgroundColor: "#5C6E83",
                // pointHoverBackgroundColor: "#5C6E83",
                borderColor: "#5C6E83",
                // pointHoverBorderColor: "#5C6E83",
            });

        this.chartConfig = {
            type: "bar",
            data: {
                labels: [""],
                datasets: datasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: "y",
                interaction: {
                    mode: 'nearest',
                    intersect: true,
                },
                scales: {
                    x: {
                        stacked: true,
                        beginAtZero: true,
                        min: 0,
                        max: 100,
                        ticks: {
                            maxTicksLimit: 5,
                            color: this.overBudget ? "#641313" : undefined,
                            stepSize: 25,
                            callback: (value: number | string, _index: number, _ticks: Tick[]): ChartConfigValue<string> | ChartConfigValue<number> | null | undefined =>
                            {
                                return percentScale(value);
                            },
                        },
                    },
                    y: { stacked: true },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            color: "#333333"
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: (items: TooltipItem<"bar">[]): string | string[] =>
                            {
                                const value = items[0].raw as string | number;
                                // return ChartingUtilities.formatPercent("", value);
                                const valueString = (typeof value === "number"
                                    ? value.toFixed(2)
                                    : value)
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "%";

                                return valueString;
                            },
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                const label = tooltipItem.label;
                                const value = Number(tooltipItem.raw);
                                return `${label}: ${formatCurrency(value, this.locale, "$")}`;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
            },
        };
    }
}
